<template>
  <AdminLayout>
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6 flex items-center gap-3">
        <div class="p-2 bg-[#22282F]/10 rounded-lg">
          <ScrollText class="w-7 h-7 text-[#22282F]" />
        </div>
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">Logs applicatifs</h1>
          <p class="text-sm text-gray-500">Export des journaux serveur par plage horaire (ZIP).</p>
        </div>
      </div>

      <!-- Export par plage -->
      <div class="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 class="font-semibold text-gray-800 mb-1">Exporter par plage horaire</h2>
        <p class="text-sm text-gray-500 mb-4">
          Seules les lignes comprises entre le début et la fin sont extraites, puis compressées.
          Le fichier n'est jamais conservé sur le serveur.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1">Début</label>
            <input type="datetime-local" v-model="start" step="1"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-600 mb-1">Fin</label>
            <input type="datetime-local" v-model="end" step="1"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>

        <div class="mt-5">
          <label class="block text-sm font-medium text-gray-600 mb-2">Fichiers à inclure</label>
          <div class="flex flex-wrap gap-4">
            <label v-for="f in bases" :key="f.base" class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="f.checked" class="w-4 h-4" />
              <span class="text-sm text-gray-700">{{ f.base }}.log</span>
            </label>
          </div>
        </div>

        <!-- Raccourcis -->
        <div class="mt-5 flex flex-wrap gap-2">
          <button v-for="p in presets" :key="p.hours" type="button" @click="applyPreset(p.hours)"
            class="px-3 py-1.5 rounded-lg text-sm border bg-white text-gray-500 border-gray-300 hover:border-[#1d2a5e]">
            {{ p.label }}
          </button>
        </div>

        <div class="mt-5 flex flex-wrap gap-3 items-center">
          <button @click="downloadZip" :disabled="busy || !canDownload"
            class="px-4 py-2 bg-[#1d2a5e] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
            <Download class="w-4 h-4" /> Télécharger le ZIP
          </button>
          <span v-if="busy" class="text-sm text-gray-400">Génération…</span>
        </div>
      </div>

      <!-- Inventaire -->
      <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 class="font-semibold text-gray-800">Fichiers disponibles</h2>
          <button @click="loadFiles" class="text-sm text-[#1d2a5e] hover:underline">Actualiser</button>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 text-gray-500">
              <tr>
                <th class="px-5 py-3 text-left font-medium">Fichier</th>
                <th class="px-5 py-3 text-left font-medium">Rotations</th>
                <th class="px-5 py-3 text-left font-medium">Taille</th>
                <th class="px-5 py-3 text-left font-medium">Dernière écriture</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="f in inventory" :key="f.base">
                <td class="px-5 py-3 font-mono text-gray-800">{{ f.base }}.log</td>
                <td class="px-5 py-3 text-gray-600">{{ f.rotations }}</td>
                <td class="px-5 py-3 text-gray-600">{{ humanSize(f.size_bytes) }}</td>
                <td class="px-5 py-3 text-gray-600">{{ formatDate(f.last_modified) }}</td>
              </tr>
              <tr v-if="!inventory.length">
                <td colspan="4" class="px-5 py-6 text-center text-gray-400">Aucun fichier.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { ScrollText, Download } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch, API_BASE_URL } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

const toast = useToast()

const bases = ref([
  { base: 'application', checked: true },
  { base: 'errors', checked: true },
  { base: 'validations', checked: true },
])
const start = ref('')
const end = ref('')
const busy = ref(false)
const inventory = ref<any[]>([])

const presets = [
  { label: 'Dernière heure', hours: 1 },
  { label: '4 dernières heures', hours: 4 },
  { label: '24 dernières heures', hours: 24 },
  { label: '7 derniers jours', hours: 24 * 7 },
]

const canDownload = computed(
  () => (start.value || end.value) && bases.value.some(b => b.checked)
)

// datetime-local attend "YYYY-MM-DDTHH:mm:ss" en heure locale du navigateur.
function toLocalInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function applyPreset(hours: number) {
  const now = new Date()
  end.value = toLocalInput(now)
  start.value = toLocalInput(new Date(now.getTime() - hours * 3600 * 1000))
}

function humanSize(n: number) {
  if (!n) return '0 o'
  const units = ['o', 'Ko', 'Mo', 'Go']
  let i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(i ? 1 : 0)} ${units[i]}`
}

function formatDate(s: string) {
  if (!s) return '—'
  try { return new Date(s).toLocaleString('fr-FR') } catch { return s }
}

async function loadFiles() {
  try {
    const res: any = await apiFetch('/api/admin/logs/files/')
    inventory.value = res.files || []
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Inventaire indisponible')
  }
}

async function downloadZip() {
  busy.value = true
  try {
    const params = new URLSearchParams()
    if (start.value) params.set('start', start.value)
    if (end.value) params.set('end', end.value)
    params.set('files', bases.value.filter(b => b.checked).map(b => b.base).join(','))

    const resp = await fetch(`${API_BASE_URL}/api/admin/logs/export/?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!resp.ok) {
      let msg = `Erreur ${resp.status}`
      try { msg = (await resp.json()).error || msg } catch { /* ignore */ }
      throw new Error(msg)
    }
    const blob = await resp.blob()
    const cd = resp.headers.get('content-disposition') || ''
    const match = cd.match(/filename="?([^"]+)"?/)
    const filename = match ? match[1] : 'ecable-logs.zip'
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Export prêt', 'Le téléchargement va démarrer.')
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Export impossible')
  } finally {
    busy.value = false
  }
}

onMounted(() => {
  applyPreset(4)
  loadFiles()
})
</script>

<style scoped>
</style>
